import { CanActivateFn, Router } from '@angular/router';
import type { UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '@meddata/core/services';

/**
 * LoginGuard is a route guard function that implements the CanActivateFn interface.
 * It determines whether a route can be activated based on the user's authentication status.
 * Using dependency injection, it accesses the AuthService to check the login status and
 * the Router service to redirect unauthorized users.
 *
 * @returns {boolean | UrlTree} Returns true if the user is logged in. Otherwise, redirects to the login page as a UrlTree object.
 */
export const LoginGuard: CanActivateFn = () => {
	const auth = inject(AuthService);
	const router = inject(Router);

	if (!auth.isLoggedIn()) return buildUnauthenticatedRedirect(auth, router);

	const user = resolveAuthenticatedUser(auth);
	if (!user) {
		auth.logout();
		return buildSessionExpiredRedirect(router);
	}

	if (auth.isFirstLogin(user)) return router.createUrlTree(['/first-login']);

	if (!auth.isUserActive(user)) {
		auth.logout();
		return router.createUrlTree(['/login'], { queryParams: { reason: 'disabled' } });
	}

	if (auth.requiresProfiling(user)) return router.createUrlTree(['/profilazione-medico']);

	return true;
};

export const FirstLoginGuard: CanActivateFn = () => {
	const auth = inject(AuthService);
	const router = inject(Router);

	if (!auth.isLoggedIn()) return buildUnauthenticatedRedirect(auth, router);

	const user = resolveAuthenticatedUser(auth);
	if (!user) {
		auth.logout();
		return buildSessionExpiredRedirect(router);
	}

	if (auth.isFirstLogin(user)) return true;

	if (!auth.isUserActive(user)) {
		auth.logout();
		return router.createUrlTree(['/login'], { queryParams: { reason: 'disabled' } });
	}

	if (auth.requiresProfiling(user)) return router.createUrlTree(['/profilazione-medico']);

	return router.createUrlTree(['/home']);
};

export const DoctorProfilingGuard: CanActivateFn = () => {
	const auth = inject(AuthService);
	const router = inject(Router);

	if (!auth.isLoggedIn()) return buildUnauthenticatedRedirect(auth, router);

	const user = resolveAuthenticatedUser(auth);
	if (!user) {
		auth.logout();
		return buildSessionExpiredRedirect(router);
	}

	if (auth.isFirstLogin(user)) return router.createUrlTree(['/first-login']);

	if (!auth.isUserActive(user)) {
		auth.logout();
		return router.createUrlTree(['/login'], { queryParams: { reason: 'disabled' } });
	}

	// Profilazione gia' completata, oppure feature disattivata: la pagina non serve.
	if (!auth.requiresProfiling(user)) return router.createUrlTree(['/home']);

	return true;
};

function resolveAuthenticatedUser(auth: AuthService) {
	let user = auth.user();
	if (!user) {
		auth.getInfoFromLocalStorage();
		user = auth.user();
	}

	return user;
}

function buildSessionExpiredRedirect(router: Router) {
	return router.createUrlTree(['/login'], { queryParams: { reason: 'session_expired' } });
}

/**
 * Redirects an unauthenticated user to the login page.
 *
 * Only flags the redirect as `session_expired` when a session token is actually
 * stored (i.e. a previously logged-in user whose token expired). A first-time or
 * incognito visitor with no stored token is sent to login without any message.
 */
function buildUnauthenticatedRedirect(auth: AuthService, router: Router) {
	if (auth.hasStoredSession()) return buildSessionExpiredRedirect(router);
	return router.createUrlTree(['/login']);
}
